import {DropShadowFilter} from "@pixi/filter-drop-shadow";
import {OutlineFilter} from "@pixi/filter-outline";
import * as PIXI from "pixi.js";
import * as AssetHelpers from "../core/AssetHelpers";
import {Container} from "../core/Container";
import {Callback, list} from "../core/Reflection";
import {Times} from "../core/Signal";
import {Sprite} from "../core/Sprite";
import {OptionId} from "../data/StoryModel";
import {ColorOverlayFilter} from "../filters/ColorOverlayFilter";
import {GameManager} from "../managers/GameManager";
import {Animation} from "./animations/Animation";
import {AnimationContext} from "./animations/AnimationContext";
import {AnimationFactory} from "./animations/AnimationFactory";
import {Optionable, OptionableData} from "./Optionable";
import {IPaperCut, PaperCutPoly, PaperCutSprite} from "./PaperCut";
import {SceneItemMask} from "./SceneItemMask";
import {StorySceneContainer} from "./StorySceneContainer";

export class SceneItem extends Container implements Optionable {

    get width(): number {
        return this._itemWidth;
    }

    set width(value: number) {
        this._itemWidth = value;
        if (this.sprite) {
            this.sprite.width = value;
        }
    }

    get height(): number {
        return this._itemHeight;
    }

    set height(value: number) {
        this._itemHeight = value;
        if (this.sprite) {
            this.sprite.height = value;
        }
    }

    public readonly option?: OptionId;
    public readonly animation?: string;
    public animationObj?: Animation;
    public sprite?: Sprite;
    public paperCut?: IPaperCut;
    public readonly items: Optionable[] = list((i: number, p: OptionableData | undefined) =>
        this.addChild(p && p.maskOf ?
            new SceneItemMask() :
            this.createSceneItem(p)));

    public textureName?: string;

    private readonly normalFilterPadding = 26;
    private readonly normalOutline = 8 * (PIXI.utils.isMobile.phone ? 0.5 : 1);

    private _itemWidth: number = 0;
    private _itemHeight: number = 0;

    public onLayout() {
        if (this.animation) {
            const children = "children:";
            if (this.animation.indexOf(children) === 0) {
                const animation = this.animation.substring(children.length);
                for (const item of this.items) {
                    if (item instanceof SceneItem && !item.animationObj) {
                        item.animationObj = AnimationFactory(animation);
                    }
                }
            } else {
                this.animationObj = AnimationFactory(this.animation);
            }
        }
    }

    public setLayoutTexture(texture: string | PIXI.Texture, anchorX: number, anchorY: number) {
        if (typeof texture === "string") {
            this.textureName = texture;
        }
        this.sprite = this.addChild(new Sprite());
        this.sprite.setLayoutTexture(texture, anchorX, anchorY);
    }

    public getPaperCut(): IPaperCut | undefined {
        if (this.sprite && !this.paperCut) {
            const textureName = this.sprite.texture.textureCacheIds[0];
            if (!textureName) {
                return undefined;
            }

            // From polygon data
            if (!this.paperCut) {
                const story = GameManager.instance.story;
                if (story.cutouts) {
                    const polygons = story.cutouts.getPolygons(textureName);
                    if (polygons && polygons.length > 0) {

                        const paperCut = this.paperCut = this.addChild(new PaperCutPoly(polygons));
                        paperCut.x = -this.sprite.anchor.x * this.sprite.width;
                        paperCut.y = -this.sprite.anchor.y * this.sprite.height;
                        paperCut.scale.x = this.sprite.scale.x / 2;
                        paperCut.scale.y = this.sprite.scale.y / 2;

                        const rs = this.getRelativeScale();
                        if (rs < 0.999) {
                            const p = this.normalOutline * (1 / rs - 1) * 0.5; // 0.5 is magic number
                            const f = new OutlineFilter(p, 0xffffff);
                            f.padding = p * 3; // fix of cutoff on mobile
                            paperCut.filters = [f];
                        }

                    } else {
                        // No default cut out in stories with polygons
                        return undefined;
                    }
                }
            }

            // From texture
            if (!this.paperCut) {
                const paperCutTextureName = textureName.replace(/\.png$/, "") + "-stroke";
                const paperCutTexture = AssetHelpers.texture(paperCutTextureName);
                if (paperCutTexture) {
                    const paperCut = this.paperCut = this.addChild(new PaperCutSprite(paperCutTexture));
                    const pt = paperCut.texture;
                    const t = this.sprite.texture;
                    const s = paperCut.texture.width < this.sprite.texture.width ? 2 : 1;
                    paperCut.anchor.set(
                        this.sprite.anchor.x + (0.5 - this.sprite.anchor.x) * (pt.width * s - t.width) / (pt.width * s),
                        this.sprite.anchor.y + (0.5 - this.sprite.anchor.y) * (pt.height * s - t.height) / (pt.height * s));
                    paperCut.scale.x = this.sprite.scale.x * s;
                    paperCut.scale.y = this.sprite.scale.y * s;

                    const rs = this.getRelativeScale();
                    if (rs < 0.999) {
                        const p = this.normalOutline * (1 / rs - 1) * 0.5; // 0.5 is magic number
                        const f = new OutlineFilter(p, 0xffffff);
                        f.padding = p;
                        paperCut.filters = [f];
                    }
                }
            }

            // From original sprite + filters
            if (!this.paperCut) {
                const paperCut = this.paperCut = this.addChild(new PaperCutSprite(this.sprite.texture));
                paperCut.anchor.set(this.sprite.anchor.x, this.sprite.anchor.y);
                paperCut.scale.x = this.sprite.scale.x;
                paperCut.scale.y = this.sprite.scale.y;
                paperCut.filters = [
                    new ColorOverlayFilter(0xffffff),
                    new OutlineFilter(this.normalOutline, 0xffffff),
                ];
                for (const filter of paperCut.filters) {
                    filter.padding = this.normalFilterPadding;
                }
            }

            this.paperCut.initX = this.paperCut.x;
            this.paperCut.initY = this.paperCut.y;
            this.paperCut.initScaleX = this.paperCut.scale.x;
            this.paperCut.initScaleY = this.paperCut.scale.y;
            return this.paperCut;
        }
        return this.paperCut;
    }

    public updateFilterScale(value: number) {
        if (this.paperCut && this.paperCut.filters) {
            value /= this.getRelativeScale();
            for (const filter of this.paperCut.filters) {
                filter.padding = this.normalFilterPadding * value;
                if (filter instanceof OutlineFilter) {
                    filter.thickness = this.normalOutline * value;
                }
                if (filter instanceof DropShadowFilter) {
                    filter.blur = 2 * value;
                    filter.distance = 5 * value;
                }
            }
        }
        if (this.items) {
            for (const item of this.items) {
                if (item.updateFilterScale) {
                    item.updateFilterScale(value);
                }
            }
        }
    }

    public setVisible(value: boolean) {
        value = value && this.parent.visible;
        this.visible = value;
        if (this.paperCut) {
            this.paperCut.visible = value;
        }
        if (value && this.sprite && this.textureName) {
            this.sprite.setLayoutTexture(this.textureName, this.sprite.anchor.x, this.sprite.anchor.y);
        }
    }

    public getVisible(): boolean {
        return this.worldVisible;
    }

    public animate(context: AnimationContext, cb: Callback): void {
        if (!this.worldVisible) {
            cb();
            return;
        }
        if (this.animation === "static") {
            cb();
            return;
        }
        const animatable = this.items.filter((it) => !!it.animate);
        const onCompleteThisAndChildren = Times(animatable.length + 1, cb);
        for (const item of animatable) {
            item.animate!(context, onCompleteThisAndChildren);
        }
        if (this.animationObj) {
            this.animationObj.animate(this, context, onCompleteThisAndChildren);
        } else {
            onCompleteThisAndChildren();
        }
    }

    public getAllPaperCuts(ar?: IPaperCut[]) {
        ar = ar || [];
        for (const item of this.items) {
            const p = item.getPaperCut && item.getPaperCut();
            if (p) {
                ar.push(p);
            }
            if (item instanceof SceneItem) {
                item.getAllPaperCuts(ar);
            }
        }
        return ar!;
    }

    public updatePaperCut() {
        if (this.paperCut && this.paperCut.worldVisible) {
            const paperLayer = this.paperCut.parent;
            this.paperCut.changeParent(this);
            this.paperCut.x = this.paperCut.initX;
            this.paperCut.y = this.paperCut.initY;
            this.paperCut.rotation = 0;
            this.paperCut.skew.set(0, 0);
            this.paperCut.scale.x = this.paperCut.initScaleX;
            this.paperCut.scale.y = this.paperCut.initScaleY;
            this.paperCut.changeParent(paperLayer);
        }
        for (const i of this.items) {
            if (i instanceof SceneItem) {
                i.updatePaperCut();
            }
        }
    }

    public paperCutVisible(value: boolean) {
        if (this.paperCut) {
            this.paperCut.alpha = value ? 1 : 0;
        }
        for (const i of this.items) {
            if (i instanceof SceneItem) {
                i.paperCutVisible(value);
            }
        }
    }

    public getRelativeScale() {
        let p = this.parent;
        let s = Math.abs(this.scale.x);
        while (p && !(p instanceof StorySceneContainer)) {
            s *= Math.abs(p.scale.x);
            p = p.parent;
        }
        return s;
    }

    protected createSceneItem(data?: OptionableData): Optionable {
        return new SceneItem();
    }
}
